import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Handshake, Plus, Pencil, Key, Copy, Warning } from '@phosphor-icons/react';
import api from '../lib/api';
import { ACCENT_DOT } from '../lib/partners';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const slugify = (name) => (name.trim().split(/\s+/)[0] || '').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 30);
const errMsg = (e, fallback) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : fallback);

function PartnerFormDialog({ open, partner, onClose, onSaved }) {
  const { tx } = useLanguage();
  const isEdit = !!partner;
  const [form, setForm] = useState({ name: '', email: '', tag: '' });
  const [tagTouched, setTagTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(partner ? { name: partner.name, email: partner.email, tag: partner.tag } : { name: '', email: '', tag: '' });
    setTagTouched(false);
  }, [open, partner]);

  const setName = (name) => setForm((f) => ({ ...f, name, tag: isEdit || tagTouched ? f.tag : slugify(name) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = isEdit
        ? await api.put(`/partners/${partner.tag}`, { name: form.name, email: form.email })
        : await api.post('/partners', form);
      onSaved(r.data, isEdit);
    } catch (err) {
      toast.error(errMsg(err, tx('Could not save partner', 'No se pudo guardar', 'Échec')));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="partner-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? tx('Edit Partner', 'Editar Socio', 'Modifier') : tx('Add Partner', 'Agregar Socio', 'Ajouter')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>{tx('Full name', 'Nombre', 'Nom')}</Label>
            <Input value={form.name} onChange={(e) => setName(e.target.value)} required placeholder="Jane Smith" data-testid="partner-name-input" />
          </div>
          <div>
            <Label>{tx('Login email', 'Correo de acceso', 'Email')}</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="jane@shipcrlogistics.com" data-testid="partner-email-input" />
          </div>
          <div>
            <Label>{tx('Signup link name', 'Nombre del enlace', 'Nom du lien')}</Label>
            <div className="flex items-center rounded-md border border-input bg-gray-50 text-sm overflow-hidden">
              <span className="px-2 text-gray-400 whitespace-nowrap">/join/…/</span>
              <input
                value={form.tag}
                disabled={isEdit}
                onChange={(e) => { setTagTouched(true); setForm({ ...form, tag: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }); }}
                className="flex-1 h-9 px-2 bg-white outline-none disabled:bg-gray-50 disabled:text-gray-500 font-mono"
                data-testid="partner-tag-input"
              />
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              {isEdit
                ? tx("The link can't change once clients are tagged with it.", 'El enlace no se puede cambiar.', 'Le lien ne peut pas changer.')
                : tx('Auto-filled from the first name — edit if you like.', 'Se llena con el primer nombre.', 'Rempli depuis le prénom.')}
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="partner-form-cancel">{tx('Cancel', 'Cancelar', 'Annuler')}</Button>
            <Button type="submit" disabled={saving} className="ship-gradient text-white" data-testid="partner-form-submit">
              {saving ? '…' : isEdit ? tx('Save', 'Guardar', 'Enregistrer') : tx('Create Partner', 'Crear Socio', 'Créer')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CredentialsDialog({ creds, onClose }) {
  const { tx } = useLanguage();
  if (!creds) return null;
  const { partner, password, login_url } = creds;
  const rows = [
    [tx('Login page', 'Página de acceso', 'Connexion'), login_url, 'login-url'],
    [tx('Email', 'Correo', 'Email'), partner.email, 'email'],
    [tx('Password', 'Contraseña', 'Mot de passe'), password, 'password'],
    [tx('Client signup link', 'Enlace de registro', "Lien d'inscription"), partner.signup_url, 'signup-url'],
  ];
  const copyAll = () => {
    const text = `${partner.name} — partner login\n${rows.map(([l, v]) => `${l}: ${v}`).join('\n')}`;
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(text))
      .then(() => toast.success(tx('Login details copied', 'Datos copiados', 'Copié')))
      .catch(() => toast.error(tx('Copy blocked by browser — select and copy manually', 'Copia bloqueada — copia manualmente', 'Copie bloquée — copiez manuellement')));
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="partner-credentials-dialog">
        <DialogHeader>
          <DialogTitle>{tx(`${partner.name}'s login`, `Acceso de ${partner.name}`, `Accès de ${partner.name}`)}</DialogTitle>
        </DialogHeader>
        <div className="flex gap-2 items-start rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
          <Warning size={16} className="shrink-0 mt-0.5" />
          {tx("Copy this now — the password won't be shown again. You can always reset it.", 'Cópialo ahora — la contraseña no se mostrará de nuevo.', 'Copiez maintenant — le mot de passe ne sera plus affiché.')}
        </div>
        <div className="space-y-2">
          {rows.map(([label, value, id]) => (
            <div key={id} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
              <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">{label}</p>
              <p className="font-mono text-sm text-gray-900 break-all" data-testid={`partner-cred-${id}`}>{value}</p>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose} data-testid="partner-credentials-close">{tx('Done', 'Listo', 'OK')}</Button>
          <Button onClick={copyAll} className="ship-gradient text-white" data-testid="partner-credentials-copy">
            <Copy size={16} className="mr-1.5" /> {tx('Copy all', 'Copiar todo', 'Tout copier')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PartnerRow({ partner, onEdit, onReset, onToggle }) {
  const { tx } = useLanguage();
  return (
    <div className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl border ${partner.active ? 'border-gray-100 bg-white' : 'border-dashed border-gray-200 bg-gray-50 opacity-70'}`} data-testid={`partner-row-${partner.tag}`}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${ACCENT_DOT[partner.accent] || ACCENT_DOT.amber}`} />
          {partner.name}
          {!partner.active && <span className="text-[10px] uppercase font-bold text-gray-400">{tx('Disabled', 'Desactivado', 'Désactivé')}</span>}
        </p>
        <p className="text-xs text-gray-500 truncate" data-testid={`partner-email-${partner.tag}`}>{partner.email}</p>
        <p className="text-[11px] font-mono text-gray-400 truncate">{partner.signup_url}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button size="sm" variant="outline" onClick={() => onEdit(partner)} data-testid={`partner-edit-${partner.tag}`}><Pencil size={14} /></Button>
        <Button size="sm" variant="outline" onClick={() => onReset(partner)} data-testid={`partner-reset-${partner.tag}`}>
          <Key size={14} className="mr-1" /> {tx('Reset password', 'Nueva contraseña', 'Réinitialiser')}
        </Button>
        <Switch checked={partner.active} onCheckedChange={(v) => onToggle(partner, v)} data-testid={`partner-active-${partner.tag}`} />
      </div>
    </div>
  );
}

export default function PartnersSettings() {
  const { tx } = useLanguage();
  const [partners, setPartners] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creds, setCreds] = useState(null);

  const load = () => api.get('/partners').then((r) => setPartners(r.data || [])).catch(() => setPartners([]));
  useEffect(() => { load(); }, []);

  const onSaved = (data, isEdit) => {
    setFormOpen(false);
    if (isEdit) toast.success(tx('Partner updated', 'Socio actualizado', 'Mis à jour'));
    else setCreds(data);
    load();
  };

  const onReset = async (p) => {
    if (!window.confirm(tx(`Reset ${p.name}'s password? Their current password will stop working.`, `¿Restablecer la contraseña de ${p.name}?`, `Réinitialiser le mot de passe de ${p.name} ?`))) return;
    try { const r = await api.post(`/partners/${p.tag}/reset-password`); setCreds(r.data); }
    catch (err) { toast.error(errMsg(err, tx('Reset failed', 'Error', 'Échec'))); }
  };

  const onToggle = async (p, active) => {
    try {
      await api.post(`/partners/${p.tag}/status`, { active });
      toast.success(active ? tx(`${p.name} enabled`, `${p.name} activado`, `${p.name} activé`) : tx(`${p.name} disabled — login blocked`, `${p.name} desactivado`, `${p.name} désactivé`));
      load();
    } catch (err) { toast.error(errMsg(err, tx('Update failed', 'Error', 'Échec'))); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="kpi-card" data-testid="partners-settings">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 ship-gradient rounded-xl"><Handshake size={24} className="text-white" /></div>
          <div>
            <h3 className="font-bold text-lg" style={{ fontFamily: 'Bebas Neue' }}>{tx('Partners', 'Socios', 'Partenaires')}</h3>
            <p className="text-sm text-gray-500">{tx('Referral partners get a view-only login, a signup link and a $/kg commission.', 'Socios con acceso de solo lectura, enlace y comisión por kg.', 'Partenaires avec accès lecture seule et commission.')}</p>
          </div>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="ship-gradient text-white rounded-lg shrink-0" data-testid="add-partner-btn">
          <Plus size={16} className="mr-1.5" /> {tx('Add Partner', 'Agregar', 'Ajouter')}
        </Button>
      </div>
      <div className="space-y-2">
        {partners.map((p) => (
          <PartnerRow key={p.tag} partner={p} onEdit={(x) => { setEditing(x); setFormOpen(true); }} onReset={onReset} onToggle={onToggle} />
        ))}
        {partners.length === 0 && <p className="text-sm text-gray-400 text-center py-6">{tx('No partners yet.', 'Aún no hay socios.', 'Aucun partenaire.')}</p>}
      </div>
      <PartnerFormDialog open={formOpen} partner={editing} onClose={() => setFormOpen(false)} onSaved={onSaved} />
      <CredentialsDialog creds={creds} onClose={() => setCreds(null)} />
    </motion.div>
  );
}
